
import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaUser, 
  FaBook, 
  FaCalendar, 
  FaClock, 
  FaChevronDown, 
  FaSearch, 
  FaSpinner,
  FaIdCard,
  FaExclamationTriangle,
  FaTimes
} from '../../../utils/iconUtils';
import { 
  getLibraryMemberByMemId,
  getLibraryBooks,
  getLibraryCategories,
  LibraryBook,
  LibraryCategory
} from '../../../firebase/libraryFirestoreService';

interface AttendanceFormProps {
  editingRecord?: {
    id?: string;
    memberId?: string;
    fullName?: string;
    bookTitle?: string;
    categoryId?: string | number;
    timeRecorded?: string;
  };
  onCancel: () => void;
  onSave: (data: any) => Promise<void>;
  loading?: boolean;
}

const AttendanceForm: React.FC<AttendanceFormProps> = ({
  editingRecord,
  onCancel,
  onSave,
  loading = false
}) => {
  const [memberId, setMemberId] = useState(
    editingRecord?.memberId ? String(editingRecord.memberId).toUpperCase() : ''
  );
  const [fullName, setFullName] = useState(editingRecord?.fullName || '');
  const [bookTitle, setBookTitle] = useState(editingRecord?.bookTitle || '');
  const [categoryId, setCategoryId] = useState<string>(
    editingRecord?.categoryId !== undefined && editingRecord?.categoryId !== null
      ? String(editingRecord.categoryId)
      : ''
  );
  const [timeRecorded, setTimeRecorded] = useState(
    editingRecord?.timeRecorded || new Date().toISOString().slice(0, 16)
  );
  
  const [memberPhoto, setMemberPhoto] = useState<string | null>(null);
  const [books, setBooks] = useState<LibraryBook[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchingMember, setSearchingMember] = useState(false);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);

  // Cache keys
  const BOOKS_CACHE_KEY = 'library_books_cache_attendance';
  const CATEGORIES_CACHE_KEY = 'library_categories_cache_attendance';
  const BOOKS_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
  const CATEGORIES_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes

  // Cache helper functions
  const isCacheValid = (cacheKey: string, expiry: number): boolean => {
    try {
      const cacheData = localStorage.getItem(cacheKey);
      if (!cacheData) return false;
      const { timestamp } = JSON.parse(cacheData);
      return Date.now() - timestamp < expiry;
    } catch {
      return false;
    }
  };

  const getCachedData = <T,>(cacheKey: string): T | null => {
    try {
      const cacheData = localStorage.getItem(cacheKey);
      if (!cacheData) return null;
      const { data } = JSON.parse(cacheData);
      return data;
    } catch {
      return null;
    }
  };

  const saveToCache = <T,>(cacheKey: string, data: T) => {
    const cacheData = {
      timestamp: Date.now(),
      data
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  };

  // Initialize data
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadCategories(), loadBooks()]);
    };
    loadData();
  }, []);

  // Filter books by category
  const filteredBooks = useMemo(() => {
    if (!categoryId) return books;
    return books.filter((book) => 
      book.categoryId != null && String(book.categoryId) === categoryId
    );
  }, [categoryId, books]);

  const loadCategories = async () => {
    if (isCacheValid(CATEGORIES_CACHE_KEY, CATEGORIES_CACHE_EXPIRY)) {
      const cachedCategories = getCachedData<LibraryCategory[]>(CATEGORIES_CACHE_KEY);
      if (cachedCategories && cachedCategories.length > 0) {
        setCategories(cachedCategories);
        return;
      }
    }

    setLoadingCategories(true);
    try {
      const result = await getLibraryCategories();
      if (result.success) {
        setCategories(result.data);
        saveToCache(CATEGORIES_CACHE_KEY, result.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadBooks = async () => {
    if (isCacheValid(BOOKS_CACHE_KEY, BOOKS_CACHE_EXPIRY)) {
      const cachedBooks = getCachedData<LibraryBook[]>(BOOKS_CACHE_KEY);
      if (cachedBooks && cachedBooks.length > 0) {
        setBooks(cachedBooks);
        return;
      }
    }

    setLoadingBooks(true);
    try {
      const result = await getLibraryBooks();
      if (result.success) {
        setBooks(result.data);
        saveToCache(BOOKS_CACHE_KEY, result.data);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoadingBooks(false);
    }
  };

  const handleMemberIdSearch = async () => {
    const trimmedId = memberId.trim();
    if (!trimmedId) {
      setError('Please enter a Member ID');
      return;
    }

    setSearchingMember(true);
    setError(null);
    setManualOverride(false);

    try {
      const memberCacheKey = `library_member_${trimmedId}`;
      const cachedMember = getCachedData<any>(memberCacheKey);
      
      if (cachedMember) {
        setFullName(cachedMember.fullName || '');
        setMemberPhoto(cachedMember.photoUrl || null);
        return;
      }

      const result = await getLibraryMemberByMemId(trimmedId);
      if (result.success && result.data) {
        setFullName(result.data.fullName);
        setMemberPhoto(result.data.photoUrl || null);
        saveToCache(memberCacheKey, {
          fullName: result.data.fullName,
          photoUrl: result.data.photoUrl
        });
      } else {
        setError(`Member ID "${trimmedId}" not found. You can enter details manually.`);
        setFullName('');
        setMemberPhoto(null);
        setManualOverride(true);
      }
    } catch (error: any) {
      setError(error.message || 'Error searching for member');
      setManualOverride(true);
    } finally {
      setSearchingMember(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && memberId.trim()) {
      e.preventDefault();
      handleMemberIdSearch();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors: string[] = [];
    
    if (!memberId.trim()) validationErrors.push('Member ID is required');
    if (!fullName.trim()) validationErrors.push('Member name is required');
    if (!bookTitle.trim()) validationErrors.push('Book title is required');
    if (!categoryId) validationErrors.push('Category is required');
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    const attendanceData = {
      memberId: memberId.trim().toUpperCase(),
      fullName: fullName.trim(),
      bookTitle: bookTitle.trim(),
      categoryId: categoryId,
      timeRecorded: new Date(timeRecorded).toISOString(),
    };

    try {
      await onSave(attendanceData);
      resetForm();
    } catch (error) {
      // Error is handled by parent
    }
  };

  const resetForm = () => {
    if (!editingRecord) {
      setMemberId('');
      setFullName('');
      setBookTitle('');
      setCategoryId('');
      setTimeRecorded(new Date().toISOString().slice(0, 16));
    }
    setMemberPhoto(null);
    setError(null);
    setManualOverride(false);
  };

  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
    if (e.target.value && !manualOverride) {
      setManualOverride(true);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-3 border border-primary-200 mb-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">
            {editingRecord ? 'Edit Attendance' : 'Add New Attendance'}
          </h2>
          <p className="text-xs text-gray-500 mt-1">Library visit record</p>
        </div>
        <button
          onClick={handleCancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={loading}
        >
          <FaTimes className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {error && (
          <div className={`p-3 rounded-lg text-sm ${error.includes('not found') ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
            <div className="flex items-start gap-2">
              <FaExclamationTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                {error}
                {error.includes('not found') && manualOverride && (
                  <p className="text-xs mt-1">You can enter details manually below.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ID, Name, Category, Book & Photo on one row (md+) */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaIdCard className="inline-block h-4 w-4 mr-2 text-primary-600" />
              Member ID
            </label>
            <div className="relative">
                  <input
                type="text"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="M001"
                className="w-full pl-3 pr-9 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleMemberIdSearch}
                disabled={searchingMember || !memberId.trim() || loading}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {searchingMember ? (
                  <FaSpinner className="h-3 w-3 animate-spin" />
                ) : (
                  <FaSearch className="h-3 w-3" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaUser className="inline-block h-4 w-4 mr-2 text-primary-600" />
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={handleFullNameChange}
              placeholder="Member name"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              readOnly={!!editingRecord && !manualOverride}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="relative">
              <FaBook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
                required
                disabled={loadingCategories || loading}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id || ''}>
                    {category.name} ({category.bookCount || 0})
                  </option>
                ))}
              </select>
              <FaChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3 pointer-events-none" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Book Title</label>
            <div className="relative">
              <FaBook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={bookTitle}
                onChange={(e) => setBookTitle(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
                disabled={loadingBooks || loading}
              >
                <option value="">Select Book</option>
                {filteredBooks.map((book) => (
                  <option key={book.id} value={book.title}>
                    {book.title}
                  </option>
                ))}
              </select>
              <FaChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3 pointer-events-none" />
            </div>
          </div>

          {/* Picture Box */}
          <div className="flex justify-center md:justify-end">
            <div className="relative">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 border-gray-200 bg-gradient-to-br from-primary-50 to-gray-50 flex items-center justify-center">
                {memberPhoto ? (
                  <img
                    src={memberPhoto}
                    alt="Member"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <FaUser className="h-10 w-10 md:h-12 md:w-12 text-gray-400" />
                )}
              </div>
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white text-xs px-2 py-0.5 rounded">Photo</div>
            </div>
          </div>
        </div>

        {/* Selected Book Preview */}
     

        {/* Date, Time, Actions aligned to top row columns (md:grid-cols-5) */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end pt-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCalendar className="inline-block h-3 w-3 mr-2 text-primary-600" />
              Date
            </label>
            <input
              type="date"
              value={timeRecorded.split('T')[0]}
              onChange={(e) => {
                const date = e.target.value;
                const time = timeRecorded.split('T')[1] || '12:00';
                setTimeRecorded(`${date}T${time}`);
              }}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaClock className="inline-block h-3 w-3 mr-2 text-primary-600" />
              Time
            </label>
            <input
              type="time"
              value={timeRecorded.split('T')[1] || '12:00'}
              onChange={(e) => {
                const time = e.target.value;
                const date = timeRecorded.split('T')[0];
                setTimeRecorded(`${date}T${time}`);
              }}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={loading}
            />
          </div>

          <div className="md:col-span-1 flex gap-2 justify-start md:justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
              disabled={loading}
            >
              {loading && <FaSpinner className="h-3 w-3 animate-spin" />}
              {editingRecord ? 'Update' : 'Save Record'}
            </button>
          </div>
        </div>

        {/* Loading States */}
        {(loadingBooks || loadingCategories) && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FaSpinner className="h-3 w-3 animate-spin" />
            <span>Loading data...</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default AttendanceForm;
